import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Form, 
    Switch, 
    Slider, 
    Select, 
    Button, 
    Space, 
    Typography, 
    Divider, 
    Row, 
    Col, 
    Card,
    message,
    Alert
} from 'antd';
import { 
    BellOutlined, 
    MailOutlined, 
    MobileOutlined, 
    DesktopOutlined, 
    SoundOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const NotificationPreferences = ({ 
    visible, 
    onClose, 
    onSave,
    showPrompt = false 
}) => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState({});

    useEffect(() => {
        if (visible) {
            fetchPreferences();
        }
    }, [visible]);

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            const response = await api.getPreferences();
            if (response.success) {
                const notificationPrefs = response.data.notification_preferences || {};
                setPreferences(notificationPrefs);
                form.setFieldsValue(notificationPrefs);
            }
        } catch (error) {
            console.error('Failed to fetch notification preferences:', error);
            message.error('Failed to load notification preferences');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        try {
            setSaving(true);
            const response = await api.updateNotificationPreferences(values);
            if (response.success) {
                setPreferences(values);
                message.success('Notification preferences saved successfully');
                onSave?.(values);
                if (!showPrompt) {
                    onClose?.();
                }
            } else {
                message.error(response.message || 'Failed to save notification preferences');
            }
        } catch (error) {
            console.error('Failed to save notification preferences:', error);
            message.error('Failed to save notification preferences');
        } finally {
            setSaving(false);
        }
    };

    const handleSkip = () => {
        onClose?.();
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <BellOutlined />
                    {showPrompt ? 'Notification Preferences' : 'Manage Notifications'}
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={800}
            footer={
                showPrompt ? (
                    <Space>
                        <Button onClick={handleSkip}>
                            Skip for now
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={() => form.submit()}
                            loading={saving}
                        >
                            Save Preferences
                        </Button>
                    </Space>
                ) : (
                    <Space>
                        <Button onClick={onClose}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={() => form.submit()}
                            loading={saving}
                        >
                            Save Changes
                        </Button>
                    </Space>
                )
            }
        >
            {showPrompt && (
                <Alert
                    message="Customize your notification experience"
                    description="Choose how you'd like to receive notifications about bookings, quotes, and updates. You can change these settings anytime."
                    type="info"
                    showIcon
                    className="mb-4"
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                initialValues={preferences}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Card 
                            title={<><MailOutlined /> Email</>} 
                            size="small"
                            className="h-full"
                        >
                            <Form.Item name={['email', 'enabled']} valuePropName="checked">
                                <Switch 
                                    checkedChildren="On" 
                                    unCheckedChildren="Off"
                                    size="small"
                                />
                            </Form.Item>
                            
                            <Divider />
                            
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Booking Updates</Text>
                                    <Form.Item name={['email', 'booking_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Quote Updates</Text>
                                    <Form.Item name={['email', 'quote_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Promotional</Text>
                                    <Form.Item name={['email', 'promotional']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">System Notifications</Text>
                                    <Form.Item name={['email', 'system_notifications']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card 
                            title={<><MobileOutlined /> Push</>} 
                            size="small"
                            className="h-full"
                        >
                            <Form.Item name={['push', 'enabled']} valuePropName="checked">
                                <Switch 
                                    checkedChildren="On" 
                                    unCheckedChildren="Off"
                                    size="small"
                                />
                            </Form.Item>
                            
                            <Divider />
                            
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Booking Updates</Text>
                                    <Form.Item name={['push', 'booking_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Quote Updates</Text>
                                    <Form.Item name={['push', 'quote_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Promotional</Text>
                                    <Form.Item name={['push', 'promotional']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">System Notifications</Text>
                                    <Form.Item name={['push', 'system_notifications']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} md={8}>
                        <Card 
                            title={<><DesktopOutlined /> In-App</>} 
                            size="small"
                            className="h-full"
                        >
                            <Form.Item name={['in_app', 'enabled']} valuePropName="checked">
                                <Switch 
                                    checkedChildren="On" 
                                    unCheckedChildren="Off"
                                    size="small"
                                />
                            </Form.Item>
                            
                            <Divider />
                            
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Booking Updates</Text>
                                    <Form.Item name={['in_app', 'booking_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Quote Updates</Text>
                                    <Form.Item name={['in_app', 'quote_updates']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">Promotional</Text>
                                    <Form.Item name={['in_app', 'promotional']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <Text className="text-sm">System Notifications</Text>
                                    <Form.Item name={['in_app', 'system_notifications']} valuePropName="checked" noStyle>
                                        <Switch size="small" />
                                    </Form.Item>
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card title={<><SoundOutlined /> Sound Settings</>} size="small">
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Form.Item name={['sound', 'enabled']} valuePropName="checked">
                                        <Switch 
                                            checkedChildren="On" 
                                            unCheckedChildren="Off"
                                            size="small"
                                        />
                                    </Form.Item>
                                    
                                    <Form.Item label="Volume" name={['sound', 'volume']}>
                                        <Slider
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            marks={{
                                                0: '0%',
                                                0.5: '50%',
                                                1: '100%'
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                
                                <Col xs={24} md={12}>
                                    <Form.Item label="Ringtone" name={['sound', 'ringtone']}>
                                        <Select size="small">
                                            <Option value="default">Default</Option>
                                            <Option value="chime">Chime</Option>
                                            <Option value="bell">Bell</Option>
                                            <Option value="notification">Notification</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default NotificationPreferences;
